import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { TrashIcon } from '@/utils/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  border-radius: var(--medium-border-radius);
  padding: var(--semi-big-padding) var(--default-padding);
  background-color: var(--white-main);

  h3,
  p {
    margin: 0;
  }

  h3 {
    font-size: var(--heading-small-font-size);
    line-height: var(--heading-small-line-height);
    font-weight: 600;
    padding-bottom: var(--xxsmall-padding);
  }

  p {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 400 !important;
    text-align: left !important;
  }

  .buttons {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const IconContainer = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  min-width: 55px;
  max-width: 55px;
  min-height: 55px;
  max-height: 55px;
  border-radius: var(--xsmall-border-radius);
  background-color: var(--purple-pale);
  color: var(--purple-text);
  fill: var(--purple-text);
`;

type SurveyPropsType = {
  isMasterSurvey: boolean;
  isSystemAccount: boolean;
  icon: React.ReactNode;
  title: string;
  onCopy: () => void;
  onEdit: () => void;
  onPreview: () => void;
  onDelete: () => void;
  canCopySurveys: boolean;
};

export const Survey: React.FC<SurveyPropsType> = ({
  isMasterSurvey,
  isSystemAccount,
  icon,
  title,
  onCopy,
  onEdit,
  onPreview,
  onDelete,
  canCopySurveys,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  return (
    <StyledContainer id={`survey-${title}`}>
      <IconContainer className="mr-4">{icon}</IconContainer>
      <FlexContainer
        direction="column"
        justify="flex-start"
        align="flex-start"
        width="100%"
      >
        <h3>{isMasterSurvey ? t('security.culture.survey.full') : title}</h3>
        <p>
          {isMasterSurvey
            ? t('security.culture.survey.description')
            : t('security.culture.survey.description.account', {
                accountName: currentAccount?.name,
              })}
        </p>
      </FlexContainer>
      <FlexContainer
        className="buttons"
        align="center"
        justify="flex-end"
        gap={8}
      >
        {((isMasterSurvey && isSystemAccount) || !isMasterSurvey) && (
          <AppButton
            id={`delete-survey-button-${title}`}
            onClick={onDelete}
            icon={<TrashIcon />}
            type="text"
            severity="secondary"
            rounded
            size="md"
            className="p-0"
          />
        )}
        {isMasterSurvey && !isSystemAccount && canCopySurveys && (
          <AppButton
            id={`copy-survey-button-${title}`}
            label={t('generic.copyEdit')}
            onClick={onCopy}
            type="outlined"
            size="md"
          />
        )}
        {((isMasterSurvey && isSystemAccount) || !isMasterSurvey) && (
          <AppButton
            id={`edit-survey-button-${title}`}
            label={t('generic.edit')}
            onClick={onEdit}
            type="outlined"
            size="md"
          />
        )}
        {
          <AppButton
            id={`preview-survey-button-${title}`}
            label={t('sc.survey.preview')}
            onClick={onPreview}
            size="md"
          />
        }
      </FlexContainer>
    </StyledContainer>
  );
};
